import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';
import { allJobs } from '../../data/jobsData';
import JobCard from './JobCard';
import JobFilters from './JobFilters';

const JobSection = () => {
  const [jobs, setJobs] = useState(allJobs);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [filters, setFilters] = useState({
    locations: [],
    types: [],
    experiences: [],
    salaries: [],
  });
  const [sortBy, setSortBy] = useState('newest');
  const [isLoading, setIsLoading] = useState(false);

  // Filter jobs berdasarkan semua kriteria
  const filterJobs = () => {
    setIsLoading(true);

    setTimeout(() => {
      let filtered = allJobs.filter((job) => {
        // Search by term
        const matchSearch =
          !searchTerm ||
          job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.tags.some((t) => t.toLowerCase().includes(searchTerm.toLowerCase()));

        // Search by location
        const matchLocation = !searchLocation || job.location === searchLocation;

        // Filter by locations
        const matchFilterLocations =
          filters.locations.length === 0 || filters.locations.includes(job.location);

        // Filter by types
        const matchTypes = filters.types.length === 0 || filters.types.includes(job.type);

        // Filter by experiences
        const matchExperiences =
          filters.experiences.length === 0 || filters.experiences.includes(job.experience);

        // Filter by salaries
        let matchSalaries = true;
        if (filters.salaries.length > 0) {
          matchSalaries = filters.salaries.some((range) => {
            if (range === '<5jt') return job.salaryNum < 5;
            if (range === '5-10jt') return job.salaryNum >= 5 && job.salaryNum <= 10;
            if (range === '10-20jt') return job.salaryNum >= 10 && job.salaryNum <= 20;
            if (range === '>20jt') return job.salaryNum > 20;
            return true;
          });
        }

        return (
          matchSearch &&
          matchLocation &&
          matchFilterLocations &&
          matchTypes &&
          matchExperiences &&
          matchSalaries
        );
      });

      // Sort jobs
      if (sortBy === 'salary-high') {
        filtered.sort((a, b) => b.salaryNum - a.salaryNum);
      } else if (sortBy === 'salary-low') {
        filtered.sort((a, b) => a.salaryNum - b.salaryNum);
      } else {
        // 'newest' - keep original order or sort by posted date
        filtered.sort((a, b) => {
          const order = { jam: 1, hari: 2, minggu: 3 };
          const getOrder = (str) => {
            if (str.includes('jam')) return order.jam;
            if (str.includes('hari')) return order.hari;
            if (str.includes('minggu')) return order.minggu;
            return 4;
          };
          return getOrder(a.posted) - getOrder(b.posted);
        });
      }

      setJobs(filtered);
      setIsLoading(false);
    }, 300);
  };

  // Efek saat filter berubah
  useEffect(() => {
    filterJobs();
  }, [searchTerm, searchLocation, filters, sortBy]);

  // Handler untuk filter change
  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      if (newFilters[filterType].includes(value)) {
        newFilters[filterType] = newFilters[filterType].filter((v) => v !== value);
      } else {
        newFilters[filterType] = [...newFilters[filterType], value];
      }
      return newFilters;
    });
  };

  // Reset semua filter
  const resetFilters = () => {
    setFilters({
      locations: [],
      types: [],
      experiences: [],
      salaries: [],
    });
    setSearchTerm('');
    setSearchLocation('');
    setSortBy('newest');
  };

  // Handler untuk klik job card
  const handleJobClick = (job) => {
    // Bisa ditambahkan modal atau navigasi ke detail job
    console.log('Job clicked:', job.title);
    // TODO: Buka modal detail job
  };

  // Kategori populer
  const popularCategories = [
    'IT & Software',
    'Marketing',
    'Finance',
    'Design',
    'HR',
    'Operations',
    'Sales',
    'Engineering',
    'Product',
    'Data',
  ];

  // Tips karier
  const careerTips = [
    {
      title: 'Cara Membuat CV ATS-Friendly',
      desc: 'Pastikan CV Anda lolos screening otomatis dengan tips ini.',
    },
    {
      title: 'Persiapan Interview Online',
      desc: 'Tips teknis & non-teknis untuk interview virtual yang sukses.',
    },
    {
      title: 'Negosiasi Gaji dengan Percaya Diri',
      desc: 'Strategi negosiasi yang efektif tanpa merusak hubungan.',
    },
  ];

  // Statistik lowongan
  const jobStats = [
    { label: 'IT & Software', value: 34, color: 'bg-zentra-500' },
    { label: 'Marketing', value: 22, color: 'bg-emerald-500' },
    { label: 'Finance', value: 18, color: 'bg-amber-500' },
    { label: 'Design', value: 14, color: 'bg-rose-500' },
    { label: 'Lainnya', value: 12, color: 'bg-cyan-500' },
  ];

  return (
    <section id="loker" className="py-16 md:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* ===== HEADER ===== */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-zentra-50 rounded-full px-4 py-1.5 mb-4">
            <Icon icon="mdi:briefcase-search" className="text-zentra-500 text-sm" />
            <span className="text-xs font-medium text-zentra-600">Lowongan Kerja</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Temukan Peluang Karier Terbaik Bersama Zentra
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto">
            Jelajahi ribuan lowongan dari perusahaan terbaik di Indonesia
          </p>
        </div>

        {/* ===== SEARCH BAR ===== */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 md:p-5 mb-8">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Icon
                icon="mdi:magnify"
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-lg"
              />
              <input
                type="text"
                placeholder="Cari posisi, perusahaan, atau keyword..."
                className="search-input w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-zentra-400 transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="relative">
              <Icon
                icon="mdi:map-marker"
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-lg"
              />
              <select
                className="search-input w-full md:w-48 pl-10 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-zentra-400 transition-all appearance-none bg-white"
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
              >
                <option value="">Semua Lokasi</option>
                <option value="Jakarta">Jakarta</option>
                <option value="Surabaya">Surabaya</option>
                <option value="Bandung">Bandung</option>
                <option value="Yogyakarta">Yogyakarta</option>
                <option value="Remote">Remote</option>
              </select>
            </div>
            <button
              className="gradient-btn text-white text-sm font-semibold px-8 py-3 rounded-xl hover:shadow-lg hover:shadow-zentra-500/25 transition-all flex items-center justify-center gap-2"
              onClick={filterJobs}
            >
              <Icon icon="mdi:magnify" className="text-base" />
              Cari
            </button>
          </div>
        </div>

        {/* ===== MAIN CONTENT ===== */}
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Filters - Left Sidebar */}
          <JobFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={resetFilters}
          />

          {/* Job Listings - Center */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            {/* Header with count and sort */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">
                <span className="font-semibold text-gray-900">{jobs.length}</span>{' '}
                lowongan ditemukan
              </p>
              <select
                className="text-xs text-gray-500 bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-zentra-400 transition-all"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="newest">Terbaru</option>
                <option value="salary-high">Gaji Tertinggi</option>
                <option value="salary-low">Gaji Terendah</option>
              </select>
            </div>

            {/* Loading State */}
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6 animate-pulse"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-xl bg-gray-200"></div>
                      <div className="flex-1">
                        <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
                        <div className="flex gap-2">
                          <div className="h-6 bg-gray-200 rounded w-16"></div>
                          <div className="h-6 bg-gray-200 rounded w-20"></div>
                          <div className="h-6 bg-gray-200 rounded w-14"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : jobs.length === 0 ? (
              /* Empty State */
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                <Icon
                  icon="mdi:briefcase-search-outline"
                  className="text-5xl text-gray-300 mx-auto mb-4"
                />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Lowongan Tidak Ditemukan
                </h3>
                <p className="text-sm text-gray-500 max-w-sm mx-auto">
                  Coba ubah filter atau kata kunci pencarian Anda. Kami yakin ada
                  posisi yang tepat untuk Anda!
                </p>
                <button
                  onClick={resetFilters}
                  className="mt-4 text-sm text-zentra-600 hover:text-zentra-700 font-medium transition-colors"
                >
                  Reset semua filter →
                </button>
              </div>
            ) : (
              /* Job Cards */
              <div className="space-y-4">
                {jobs.map((job) => (
                  <JobCard key={job.id} job={job} onClick={handleJobClick} />
                ))}
              </div>
            )}
          </div>

          {/* Right Sidebar - Categories & Tips */}
          <div className="lg:col-span-1 order-3">
            {/* Popular Categories */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Icon icon="mdi:fire" className="text-zentra-500" />
                Kategori Populer
              </h3>
              <div className="flex flex-wrap gap-2">
                {popularCategories.map((cat) => (
                  <button
                    key={cat}
                    className="category-chip text-xs font-medium px-3 py-1.5 rounded-full border border-gray-200 text-gray-600 bg-white hover:bg-zentra-600 hover:text-white hover:border-zentra-600 transition-all"
                    onClick={() => setSearchTerm(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Career Tips */}
            <div className="bg-gradient-to-br from-zentra-600 to-zentra-800 rounded-2xl p-5 mb-6">
              <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <Icon icon="mdi:lightbulb-on" className="text-amber-400" />
                Tips Karier
              </h3>
              <div className="space-y-3">
                {careerTips.map((tip, i) => (
                  <div
                    key={i}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/10 hover:bg-white/20 transition-colors cursor-pointer"
                  >
                    <p className="text-xs font-medium text-white mb-1">{tip.title}</p>
                    <p className="text-xs text-white/60">{tip.desc}</p>
                    <button className="text-xs text-zentra-200 font-medium mt-2 hover:text-white transition-colors">
                      Baca selengkapnya →
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Job Statistics */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Statistik Lowongan
              </h3>
              <div className="space-y-4">
                {jobStats.map((stat, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-gray-600">{stat.label}</span>
                      <span className="font-semibold text-gray-900">{stat.value}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${stat.color} rounded-full transition-all duration-500`}
                        style={{ width: `${stat.value}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JobSection;
